import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onCommand } from '../lib/commandBus';
import { exportDomains } from '../lib/exports';
import {
  hideDomain,
  loadCompanies,
  loadHiddenDomains,
  loadScrapePrefs,
  mergeCompanies,
  saveCompanies,
  saveHiddenDomains,
  saveScrapePrefs,
  saveScrapeSession,
  unhideDomain,
  upsertScrapeSessionCompany,
  visibleCompanies,
} from '../lib/storage';
import ExternalLink from './ExternalLink';
import { DataTable, Td, TdAction, Tr } from './DataTable';
import ScrapeProductHuntModal from './ScrapeProductHuntModal';

const DEFAULT_SCRAPE = {
  limit: 120,
  order: 'NEWEST',
  postedAfter: '',
  postedBefore: '',
  featured: false,
  topic: '',
};

function scrapeDateParams(p) {
  const after = String(p.postedAfter || '').trim();
  const before = String(p.postedBefore || '').trim();
  if (after && before) {
    return { postedAfter: after, postedBefore: before };
  }
  if (before) {
    return { postedAfter: before, postedBefore: before };
  }
  if (after) {
    return { postedAfter: after };
  }
  return {};
}

function DomainsView({
  companies,
  onCompaniesChange,
  onScrapeSessionChange,
  hiddenDomains,
  onHiddenChange,
  onRunningChange,
  emails = [],
}) {
  const prefs = loadScrapePrefs(DEFAULT_SCRAPE);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);
  const [limit, setLimit] = useState(prefs.limit ?? 120);
  const [order, setOrder] = useState(prefs.order || 'NEWEST');
  const [postedAfter, setPostedAfter] = useState(prefs.postedAfter || '');
  const [postedBefore, setPostedBefore] = useState(
    prefs.postedBefore || ''
  );
  const [featured, setFeatured] = useState(Boolean(prefs.featured));
  const [topic, setTopic] = useState(prefs.topic || '');
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ index: 0, total: 0 });
  const [launchCount, setLaunchCount] = useState(null);
  const [error, setError] = useState(null);
  const [sessionNew, setSessionNew] = useState(0);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [blacklistDraft, setBlacklistDraft] = useState('');
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const sourceRef = useRef(null);
  const listRef = useRef(null);
  const blacklistRef = useRef(null);
  const sessionAccRef = useRef([]); // authoritative this-scrape list (avoids lost setState races)
  // Sync prefs for assistant: set_scrape_prefs + start_scrape in one tick must see new limit
  const prefsRef = useRef({
    limit: prefs.limit ?? 120,
    order: prefs.order || 'NEWEST',
    postedAfter: prefs.postedAfter || '',
    postedBefore: prefs.postedBefore || '',
    featured: Boolean(prefs.featured),
    topic: prefs.topic || '',
  });
  prefsRef.current = { limit, order, postedAfter, postedBefore, featured, topic };

  const shownCompanies = useMemo(
    () => visibleCompanies(companies, hiddenDomains),
    [companies, hiddenDomains]
  );

  const contactCountByDomain = useMemo(() => {
    const map = new Map();
    emails.forEach((row) => {
      if (!row?.domain) return;
      map.set(row.domain, (map.get(row.domain) || 0) + 1);
    });
    return map;
  }, [emails]);

  const stopStream = () => {
    const source = sourceRef.current;
    sourceRef.current = null;
    if (source) source.close();
  };

  const setRunningSafe = (value) => {
    setRunning(value);
    onRunningChange?.(value);
  };

  // Do NOT close the stream on unmount — view stays mounted across page switches.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [rows]);

  useEffect(() => {
    saveScrapePrefs({
      limit,
      order,
      postedAfter,
      postedBefore,
      featured,
      topic,
    });
  }, [limit, order, postedAfter, postedBefore, featured, topic]);

  useEffect(() => {
    const onDoc = (e) => {
      if (blacklistRef.current && !blacklistRef.current.contains(e.target)) {
        setBlacklistOpen(false);
      }
    };
    if (blacklistOpen) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [blacklistOpen]);

  const flushSession = (list) => {
    const saved = saveScrapeSession(list);
    onScrapeSessionChange?.(saved);
    return saved;
  };

  const startScrape = (override) => {
    if (
      override &&
      typeof override === 'object' &&
      ('limit' in override ||
        'order' in override ||
        'postedAfter' in override ||
        'postedBefore' in override ||
        'featured' in override ||
        'topic' in override)
    ) {
      const next = { ...prefsRef.current, ...override };
      if (override.postedAfter != null) next.postedAfter = String(override.postedAfter);
      if (override.postedBefore != null) next.postedBefore = String(override.postedBefore);
      if (
        override.postedAfter &&
        override.postedBefore == null &&
        !('postedBefore' in override)
      ) {
        next.postedBefore = String(override.postedAfter);
      }
      prefsRef.current = next;
      if (next.limit != null) setLimit(next.limit);
      if (next.order != null) setOrder(next.order);
      if (next.postedAfter != null) setPostedAfter(next.postedAfter);
      if (next.postedBefore != null) setPostedBefore(next.postedBefore);
      if (next.featured != null) setFeatured(Boolean(next.featured));
      if (next.topic != null) setTopic(next.topic);
      saveScrapePrefs(next);
    }
    const p = prefsRef.current;
    const scrapeLimit = Math.max(1, Math.min(300, Number(p.limit) || 120));
    const scrapeOrder = p.order || 'NEWEST';
    const scrapeFeatured = Boolean(p.featured);
    const scrapeTopic = (p.topic || '').trim();
    const dates = scrapeDateParams(p);

    stopStream();
    setRunningSafe(true);
    setError(null);
    setRows([]);
    setSessionNew(0);
    sessionAccRef.current = [];
    flushSession([]);
    setLaunchCount(null);
    setProgress({ index: 0, total: 0 });
    setStep(1);

    const before = new Set(loadCompanies().map((c) => c.domain));
    const hidden = new Set(loadHiddenDomains());
    const params = new URLSearchParams({
      limit: String(scrapeLimit),
      order: scrapeOrder,
    });
    if (dates.postedAfter) params.set('postedAfter', dates.postedAfter);
    if (dates.postedBefore) params.set('postedBefore', dates.postedBefore);
    if (scrapeFeatured) params.set('featured', 'true');
    if (scrapeTopic) params.set('topic', scrapeTopic);

    const source = new EventSource(`/api/scrape-producthunt?${params.toString()}`);
    sourceRef.current = source;

    source.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      if (payload.type === 'step') {
        setStep(payload.step);
        return;
      }
      if (payload.type === 'launches') {
        setLaunchCount(payload.count);
        return;
      }
      if (payload.type === 'domain') {
        const domain = payload.domain ? String(payload.domain).toLowerCase() : null;
        const company = {
          domain,
          product_name: payload.product_name || payload.name || '',
          tagline: payload.tagline || '',
          description: payload.description || '',
          votes_count: payload.votes_count || 0,
          ph_url: payload.ph_url || '',
          slug: payload.slug || '',
          website: payload.website || payload.original_url || '',
          launchedAt: payload.launchedAt || payload.featuredAt || payload.createdAt || '',
        };
        setProgress({ index: payload.index, total: payload.total });
        setRows((prev) => [
          ...prev,
          {
            name: company.product_name,
            domain,
            tagline: company.tagline,
            duplicate: domain ? before.has(domain) : false,
            hidden: domain ? hidden.has(domain) : false,
          },
        ]);
        if (domain) {
          const wasNew = !before.has(domain);
          before.add(domain);
          // Archive + this-scrape working set (ref + Redis via store — no lost races)
          onCompaniesChange(mergeCompanies([company], { prependNew: true }));
          sessionAccRef.current = upsertScrapeSessionCompany(company);
          onScrapeSessionChange?.(sessionAccRef.current);
          if (wasNew && !hidden.has(domain)) setSessionNew((n) => n + 1);
        }
        return;
      }
      if (payload.type === 'done') {
        // Final sync from server unique companies if present
        const fromServer = Array.isArray(payload.companies) ? payload.companies : null;
        if (fromServer && fromServer.length) {
          sessionAccRef.current = flushSession(fromServer);
        } else {
          flushSession(sessionAccRef.current);
        }
        setRunningSafe(false);
        stopStream();
        return;
      }
      if (payload.type === 'error') {
        setError(payload.message || 'Scrape failed');
        setRunningSafe(false);
        stopStream();
      }
    };

    source.onerror = () => {
      if (!sourceRef.current) return;
      setError((prev) => prev || 'Connection lost while scraping');
      setRunningSafe(false);
      stopStream();
    };
  };

  const stopScrape = () => {
    setRunningSafe(false);
    stopStream();
  };

  const startScrapeRef = useRef(startScrape);
  startScrapeRef.current = startScrape;
  const stopScrapeRef = useRef(stopScrape);
  stopScrapeRef.current = stopScrape;

  useEffect(() => {
    const offScrape = onCommand('start_scrape', () => startScrapeRef.current?.());
    const offStop = onCommand('stop_scrape', () => stopScrapeRef.current?.());
    const offPrefs = onCommand('set_scrape_prefs', (incoming) => {
      if (!incoming || typeof incoming !== 'object') return;
      const next = { ...prefsRef.current };
      if (incoming.limit != null) {
        next.limit = Number(incoming.limit) || 120;
        setLimit(next.limit);
      }
      if (incoming.order != null) {
        next.order = String(incoming.order);
        setOrder(next.order);
      }
      if (incoming.postedAfter != null) {
        next.postedAfter = String(incoming.postedAfter);
        setPostedAfter(next.postedAfter);
      }
      if (incoming.postedBefore != null) {
        next.postedBefore = String(incoming.postedBefore);
        setPostedBefore(next.postedBefore);
      }
      if (incoming.featured != null) {
        next.featured = Boolean(incoming.featured);
        setFeatured(next.featured);
      }
      if (incoming.topic != null) {
        next.topic = String(incoming.topic);
        setTopic(next.topic);
      }
      prefsRef.current = next;
      saveScrapePrefs(next);
    });
    return () => {
      offScrape();
      offStop();
      offPrefs();
    };
  }, []);

  const handleDownloadSession = () => {
    exportDomains(shownCompanies);
  };

  const handleClear = () => {
    if (!window.confirm('Clear all domains from this list?')) return;
    onCompaniesChange(saveCompanies([]));
    onScrapeSessionChange?.([]);
  };

  const handleHide = (domain) => {
    const next = hideDomain(domain);
    onHiddenChange(next);
    setRows((prev) =>
      prev.map((row) =>
        row.domain === domain ? { ...row, hidden: true } : row
      )
    );
  };

  const handleUnhide = (domain) => {
    onHiddenChange(unhideDomain(domain));
  };

  const tableRows = useMemo(() => {
    if (rows.length > 0) {
      return rows.map((row, i) => ({
        key: `${row.domain || row.name}-${i}`,
        name: row.name || row.domain || '—',
        domain: row.domain || '',
        website: row.domain || '',
        tagline: row.tagline || '',
        contacts: row.domain ? contactCountByDomain.get(row.domain) || 0 : 0,
        hidden: Boolean(row.hidden),
        duplicate: Boolean(row.duplicate),
      }));
    }
    return shownCompanies.map((company) => ({
      key: company.domain,
      name: company.product_name || company.domain,
      domain: company.domain,
      website: company.website || company.domain,
      tagline: company.tagline || '',
      contacts: contactCountByDomain.get(company.domain) || 0,
      hidden: false,
      duplicate: false,
    }));
  }, [rows, shownCompanies, contactCountByDomain]);

  const addBlacklist = (e) => {
    e.preventDefault();
    const value = blacklistDraft.trim().toLowerCase().replace(/^www\./, '');
    if (!value) return;
    onHiddenChange(hideDomain(value));
    setBlacklistDraft('');
  };

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="px-8 pt-8 pb-4 border-b border-paper-line">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[40px] font-bold tracking-tight text-ink leading-[1.15]">
              Domains
            </h1>
            <p className="text-[14px] text-ink-muted mt-1.5 max-w-xl">
              Scrape Product Hunt. Each domain is an object — contacts attach next, then emails.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative" ref={blacklistRef}>
              <button
                type="button"
                onClick={() => setBlacklistOpen((v) => !v)}
                className="text-[13px] text-ink-muted hover:text-ink hover:bg-wash rounded-[4px] px-1.5 py-1"
              >
                Blacklist
                <span className="ml-1.5 text-[12px] text-ink-faint tabular-nums">
                  {hiddenDomains.length}
                </span>
              </button>
              {blacklistOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-72 bg-white border border-paper-line rounded-md shadow-popover z-50 p-3">
                  <div className="text-[12px] font-medium text-ink mb-2">Hidden domains</div>
              <p className="text-[12px] text-ink-faint mb-2">
                    Still in the list. Hidden from Contacts, Emails, and CSV.
                  </p>
                  <form onSubmit={addBlacklist} className="flex gap-1.5 mb-2">
                    <input
                      value={blacklistDraft}
                      onChange={(e) => setBlacklistDraft(e.target.value)}
                      placeholder="domain.com"
                      className="flex-1 px-2 py-1 text-[12px] border border-paper-line rounded-md"
                    />
                    <button
                      type="submit"
                      className="px-2 py-1 text-[12px] rounded-md border border-paper-strong bg-white hover:bg-wash text-ink"
                    >
                      Add
                    </button>
                  </form>
                  <ul className="max-h-48 overflow-y-auto space-y-1">
                    {hiddenDomains.length === 0 ? (
                      <li className="text-[11px] text-ink-faint py-2">Empty</li>
                    ) : (
                      hiddenDomains.map((d) => (
                        <li
                          key={d}
                          className="flex items-center gap-2 text-[11px] py-1 border-b border-paper-line/60"
                        >
                          <span className="flex-1 truncate text-ink">{d}</span>
                          <button
                            type="button"
                            onClick={() => handleUnhide(d)}
                            className="text-ink-faint hover:text-ink"
                          >
                            Unhide
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                  {hiddenDomains.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Clear entire blacklist?')) {
                          onHiddenChange(saveHiddenDomains([]));
                        }
                      }}
                      className="mt-2 text-[11px] text-ink-faint hover:text-ink"
                    >
                      Clear blacklist
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setScrapeOpen(true)}
            disabled={running}
            className="text-[13px] px-3 py-1.5 rounded-[4px] bg-cta text-white hover:bg-cta-hover disabled:opacity-50"
          >
            {running ? 'Scraping…' : 'Scrape Product Hunt'}
          </button>
          {running && (
            <button
              type="button"
              onClick={() => {
                stopStream();
                setRunningSafe(false);
              }}
              className="text-[13px] px-3 py-1.5 rounded-[4px] border border-paper-strong bg-white hover:bg-wash"
            >
              Stop
            </button>
          )}
          <button
            type="button"
            onClick={handleClear}
            disabled={running || companies.length === 0}
            className="text-[13px] text-ink-muted hover:text-ink disabled:opacity-40"
          >
            Clear domains
          </button>
        </div>

        {running && progress.total > 0 ? (
          <div className="mt-3 h-1 bg-paper-line rounded-full overflow-hidden">
            <div
              className="h-full bg-ink transition-all duration-300"
              style={{
                width: `${Math.round((progress.index / progress.total) * 100)}%`,
              }}
            />
          </div>
        ) : null}

        <div className="mt-3 flex gap-4 text-[12px] text-ink-faint tabular-nums">
          <span>{shownCompanies.length} domains</span>
          <span>{emails.length} contacts attached</span>
          <span>{hiddenDomains.length} hidden</span>
          {sessionNew > 0 && <span>{sessionNew} new</span>}
          {launchCount != null && <span>{launchCount} launches</span>}
          {running && step === 1 ? <span>Grabbing launches…</span> : null}
          {running && step === 2 ? <span>Getting domains…</span> : null}
        </div>
      </header>

      {error && (
        <div className="mx-8 mt-4 px-3 py-2 text-[12px] border border-paper-line bg-callout text-ink">
          {error}
        </div>
      )}

      <DataTable
        bodyRef={listRef}
        trailing={
          <button
            type="button"
            onClick={handleDownloadSession}
            disabled={shownCompanies.length === 0}
            className="text-[12px] text-ink-muted hover:text-ink hover:bg-wash rounded-[4px] px-1.5 py-0.5 disabled:opacity-40"
          >
            Download CSV
          </button>
        }
        columns={[
          { key: 'name', label: 'Name', first: true },
          { key: 'domain', label: 'Domain' },
          { key: 'tagline', label: 'Tagline' },
          { key: 'contacts', label: 'Contacts', align: 'right', width: '7.5rem' },
          { key: 'actions', label: '', last: true, align: 'right', width: '4rem' },
        ]}
        empty={
          tableRows.length === 0
            ? running
              ? 'Fetching launches…'
              : 'Scrape Product Hunt to fill this table.'
            : null
        }
      >
        {tableRows.map((row) => (
          <Tr key={row.key} muted={row.hidden}>
            <Td first className="text-ink font-medium">
              <span className="block truncate max-w-[16rem]">{row.name}</span>
            </Td>
            <Td>
              {row.domain ? (
                <ExternalLink href={row.website || row.domain}>{row.domain}</ExternalLink>
              ) : (
                <span className="text-ink-faint">—</span>
              )}
              {row.duplicate ? (
                <span className="text-ink-faint"> · already in list</span>
              ) : null}
            </Td>
            <Td className="text-ink-muted">
              <span className="block truncate max-w-[28rem]">{row.tagline || '—'}</span>
            </Td>
            <Td align="right" className="whitespace-nowrap">
              {row.contacts > 0 ? (
                <>
                  <span className="font-semibold tabular-nums text-ink">{row.contacts}</span>
                  <span className="ml-1 text-[12px] text-ink-muted">
                    {row.contacts === 1 ? 'contact' : 'contacts'}
                  </span>
                </>
              ) : (
                <span className="text-ink-faint">—</span>
              )}
            </Td>
            <TdAction>
              {row.domain && !row.hidden ? (
                <button
                  type="button"
                  onClick={() => handleHide(row.domain)}
                  className="text-[12px] text-ink-muted hover:text-ink"
                >
                  Hide
                </button>
              ) : null}
            </TdAction>
          </Tr>
        ))}
      </DataTable>

      <ScrapeProductHuntModal
        open={scrapeOpen}
        onClose={() => setScrapeOpen(false)}
        prefs={{ limit, order, postedBefore, featured, topic }}
        onPrefsChange={(next) => {
          setLimit(next.limit);
          setOrder(next.order);
          setPostedAfter('');
          setPostedBefore(next.postedBefore);
          setFeatured(next.featured);
          setTopic(next.topic);
        }}
        onScrape={startScrape}
        running={running}
      />
    </div>
  );
}

export default DomainsView;
